import React, { useEffect, useState } from 'react';
import { AircraftFilter } from './AircraftFilter';
import SearchFilter from './SearchFilter';
import StationFilter from './StationFilter';
import OtsStartDateFilter from './OtsStartDateFilter';
import FilterNavButtons from './FilterNavButtons';
import './FilterNavigation.scss';
import { ClosedParamsErrorState, ClosedSearchParams, ClosedSortingParams } from '../../../Models/ClosedSearchParams';
import { getClosedEvents, getClosedEventsFilterCount, getClosedEventsReport  } from '../../../services/otsEventService';
import { useAuthentication } from "meportal/AuthenticationProvider";
import { convertClosedSearchParamsForApiCall } from '../../../helpers/Util';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters } from "../../../store/feature/ClosedFilterSortSlice";
import ClosedFilterModal from './FilterDrawer';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useToast } from '../../../providers/ToastProvider';
import { NOTIFICATION_TYPES } from '../../ToastNotification/toastConstants';
import { useAircraftList } from '../../../providers/AircraftListProvider';

interface FilterNavigationProps {
    filteredClosedData: (data: any) => void;
    continousToken: (token: string) => void;
    goToFirstPage: (reset: boolean) => void;
    filteredCount: (count: number) => void;
    sortButtonClicked: boolean;
    resetSortChangeButtonClicked: (click: boolean) => void;
    onDrawerStateChange?: (isOpen: boolean) => void;
    filteredAircraftHasdata: boolean;
}

const FilterNavigation: React.FC<FilterNavigationProps> = ({
    filteredClosedData,
    continousToken,
    goToFirstPage,
    filteredCount,
    sortButtonClicked,
    resetSortChangeButtonClicked,
    onDrawerStateChange,
    filteredAircraftHasdata 
}) => {
    const dispatch = useDispatch();
    const { userData, getLatestAccessToken } = useAuthentication();
    const { validAircraftList } = useAircraftList();
    const defaultSearchParams: ClosedSearchParams = {
        aircraftRegistration: '',
        problemText: '',
        station: '',
        outOfServiceTimeStart: '',
        outOfServiceTimeEnd: '',
        ataCode: '',
        eventType: [],
        fleetType: ''
    };
    const [searchParams, setSearchParams] = useState<ClosedSearchParams>(defaultSearchParams);
    const [errorState, setErrorState] = useState<ClosedParamsErrorState>({
        isErrorTail: false,
        isErrorStation: false,
        isOTSStartDateError: false,
        isATAError: false,
        isFleetTypeError: false
    });
    const currentActiveSearchedParams: ClosedSearchParams = useSelector((state: any) => state.closedFilterSort.filters);
    const sortParams: ClosedSortingParams = useSelector((state: any) => state.closedFilterSort.sorting);
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [enableReportDownload, setEnableReportDownload] = useState<boolean>(false);
    const [downloadInprogress, setDownloadInprogress] = useState<boolean>(false);
    const { addToast } = useToast();

    const handleOpenFilterDrawer = () => {
        setIsFilterDrawerOpen(true);
        if (onDrawerStateChange) {
            onDrawerStateChange(true);
        }
    };
    
    const handleCloseFilterDrawer = () => {
        setIsFilterDrawerOpen(false);
        if (onDrawerStateChange) {
            onDrawerStateChange(false);
        }
    };
    
    //setting the searchParams from redux & enable the download button if any of the filters has value
    useEffect(() => {
        const hasValue = Object.values(currentActiveSearchedParams).some(param => Array.isArray(param) ? param.length > 0 : param);
        setEnableReportDownload(hasValue);

        setSearchParams({
            ...defaultSearchParams,
            ...currentActiveSearchedParams
        });
    }, [currentActiveSearchedParams]);


const handleInputChange = (field: keyof ClosedSearchParams) => (value: string | string[]) => {
  const updatedParams = { ...searchParams, [field]: value };
  setSearchParams(updatedParams);
};

const handleDateChange = (start: string, end: string) => {
  const updatedParams = { 
    ...searchParams, 
    outOfServiceTimeStart: start, 
    outOfServiceTimeEnd: end 
  };
  setSearchParams(updatedParams);
};

    useEffect(() => {
        if (sortButtonClicked && sortParams) {
            handleSearch(isSearchDisabled ? currentActiveSearchedParams : searchParams);
            if (isSearchDisabled) {
                setSearchParams({
                    ...defaultSearchParams,
                    ...currentActiveSearchedParams
                });
            }
        }
    }, [sortButtonClicked, sortParams]);

    
     const handleErrorState = React.useCallback(
    (field: keyof ClosedParamsErrorState) => (value: boolean) => {
      setErrorState(prev => ({ ...prev, [field]: value }));
    },
    []
  );


    const onSearchClick = () => { 
        handleSearch(searchParams);
    };

    const handleSearch = async (search: ClosedSearchParams) => {
        try {
            let updatedSearchParams = convertClosedSearchParamsForApiCall(search);

            const token = await getLatestAccessToken();
            userData.access_token = token;

            const response = await getClosedEvents(userData, updatedSearchParams, sortParams);
            if (response) {
                updateActiveSearchedParams(response.continousToken, response.outOfServiceRecoveryResponse);

                if (Object.values(updatedSearchParams).some(param => Array.isArray(param) ? param.length > 0 : param)) {
                    if (response.continousToken) {
                        const token = await getLatestAccessToken();
                        userData.access_token = token;
                        const countResponse = await getClosedEventsFilterCount(userData, updatedSearchParams);
                        filteredCount(countResponse);
                    } else {
                        filteredCount(response.outOfServiceRecoveryResponse.length);
                    }
                } else {
                    filteredCount(0);
                }
            } else {
                updateActiveSearchedParams("", []);
                filteredCount(0);
            }
        } catch (error) {
            updateActiveSearchedParams("", []);
            filteredCount(0);
            console.error('Error fetching closed events:', error);
        } finally {
            resetSortChangeButtonClicked(false);
            dispatch(setFilters(search));
        }
    };

   const handleClearAll = () => {
  const clearedParams = {
    ...defaultSearchParams,
    outOfServiceTimeStart: '',
    outOfServiceTimeEnd: ''
  };
  setSearchParams(clearedParams);
  dispatch(setFilters(clearedParams));
  handleSearch(clearedParams);
   setErrorState(prev => ({
    ...prev,
    isErrorStation: false,
    isOTSStartDateError: false,
    isATAError: false,
    isFleetTypeError: false,
  }));
};


    const updateActiveSearchedParams = (token: string, searchData: any) => {
        continousToken(token);
        goToFirstPage(true);
        filteredClosedData(searchData);
    };

    const getFilterCount = (params: ClosedSearchParams) => {
        // Track 'ataCode', and 'eventType'
        const { ataCode, eventType, fleetType } = params;
        let count = 0;
        if (ataCode && ataCode !== '') count += 1;
        if (Array.isArray(eventType) && eventType.length > 0) count += 1;
        if (fleetType && fleetType !== '') count += 1;
      
        return count;
      };
      
    const filterCount = getFilterCount(searchParams);    

    const isClearAllDisabled = !Object.entries(searchParams).some(([key, param]) => {
    if (key === 'outOfServiceTimeStart' || key === 'outOfServiceTimeEnd') {
        return param !== ''; // True if either date is set
    }
    return Array.isArray(param) ? param.length > 0 : param !== '';
    });

    const isSearchDisabled = Object.values(errorState).some(error => error);

    const onDownloadClick = async () => {
        try {
            // set the searchParams to previously selected filters
            setSearchParams({
                ...defaultSearchParams,
                ...currentActiveSearchedParams
            });

            setDownloadInprogress(true);
            const updatedSearchParams = convertClosedSearchParamsForApiCall(currentActiveSearchedParams);
            const token = await getLatestAccessToken();
            userData.access_token = token;
            dayjs.extend(utc);
            dayjs.extend(timezone);
            const localTimeZone = dayjs.tz.guess();
            const response = await getClosedEventsReport(userData, updatedSearchParams, sortParams, localTimeZone);

            if (!response.ok) {
                throw new Error("Failed to download file");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);    
            const a = document.createElement("a");
            a.href = url;
            const currentDateTime = dayjs().format('MM-DD-YY_HH-mm');
            a.download = `ClosedEventReport_${currentDateTime}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading closed event report', error);
            addToast(
              NOTIFICATION_TYPES.WARNING,
              'Download failed',
              'Unable to download the closed events report. Please try again.'
            );
        }finally {
            setDownloadInprogress(false);
        }
    };

    //Enter key search functionality
    useEffect(() => {
        const handleGlobalKeyDown = (event: KeyboardEvent) => {
          if (event.key === 'Enter' && !isSearchDisabled) {
            onSearchClick();
            handleCloseFilterDrawer();
          }
        };
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => {
          window.removeEventListener('keydown', handleGlobalKeyDown);
        };
      }, [isSearchDisabled, onSearchClick]);

    return (
        <div className="filter-box">
            <div className="filter-options">             
               
                
                        <AircraftFilter
                            validAircraftList={validAircraftList}
                            aircraftRegistrationValue={searchParams.aircraftRegistration}
                            onAircraftValueChange={handleInputChange('aircraftRegistration')}
                            onAircraftError={handleErrorState('isErrorTail')}
                        />                       

                        <StationFilter
                            stationValue={searchParams.station}
                            onStationValueChange={handleInputChange('station')}
                            onStationError={handleErrorState('isErrorStation')}
                            error={errorState.isErrorStation}
                            />
                        <OtsStartDateFilter
                            startDate={searchParams.outOfServiceTimeStart}
                            endDate={searchParams.outOfServiceTimeEnd}
                            onOtsDateChange={handleDateChange}
                            onOTSStartDateError={handleErrorState('isOTSStartDateError')}
                            error={errorState.isOTSStartDateError}
                            />

                         {!isFilterDrawerOpen && (
                    <> 
                    <SearchFilter
                    searchFilterValue={searchParams.problemText}
                    onSearchFilterValueChange={handleInputChange('problemText')}/>                       
                    </>
                )}               
            </div>
            {!isFilterDrawerOpen && (<FilterNavButtons
                onClearAll={handleClearAll}
                onSearch={onSearchClick}
                isDisabledSearch={isSearchDisabled}
                isDisabledClear={isClearAllDisabled}
                onOpenAllFilters={handleOpenFilterDrawer}
                onDownloadClick={onDownloadClick} 
                isDownloadEnable = {enableReportDownload && filteredAircraftHasdata} 
                showSpinner = {downloadInprogress}
                filterCount={filterCount} 
            /> )}
            <ClosedFilterModal
                open={isFilterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                searchParams={searchParams}
                handleInputChange={handleInputChange}
                handleErrorState={handleErrorState}
                onClearAll={handleClearAll}
                onSearch={onSearchClick}
                isDisabledSearch={isSearchDisabled}
                isDisabledClear={isClearAllDisabled}
                errorState={errorState}
            />
        </div>
    );
    
};

export default FilterNavigation;
